import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { Plus, X, ShieldCheck, Trash2, Pencil, LayoutGrid, List, Eye, Download } from "lucide-react";
import { fmtDate, fmtCurrency } from "@/lib/constants";
import { BulkBar, RowCheckbox, bulkDelete } from "@/components/BulkBar";

export default function AMC() {
  const [amcs, setAmcs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("list");
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    const [a, c] = await Promise.allSettled([api.get("/amcs"), api.get("/customers")]);
    if (a.status === "fulfilled") { setAmcs(a.value.data); setErr(""); }
    else setErr(formatApiError(a.reason));
    // Customers list only powers the create/edit form — don't block the AMC list if it fails
    if (c.status === "fulfilled") setCustomers(c.value.data);
  };
  useEffect(() => { load(); }, []);

  const ids = amcs.map((a) => a.id);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const someSelected = ids.some((id) => selected.has(id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) ids.forEach((id) => n.delete(id));
    else ids.forEach((id) => n.add(id));
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const doBulkDelete = async () => {
    const idsArr = Array.from(selected);
    if (!window.confirm(`Delete ${idsArr.length} AMC contract(s)?`)) return;
    setBulkBusy(true);
    const ok = await bulkDelete(idsArr, (id) => api.delete(`/amcs/${id}`));
    setBulkBusy(false); clearSel(); await load();
    if (ok < idsArr.length) setErr(`${idsArr.length - ok} deletion(s) failed.`);
  };

  const openPO = async (a, download = false) => {
    try {
      const r = await api.get(`/files/${a.po_document_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (download) {
        const el = document.createElement("a");
        el.href = url; el.download = a.po_document_name || `PO-${a.po_number || a.contract_no || a.id}`;
        document.body.appendChild(el); el.click(); el.remove();
      } else {
        window.open(url, "_blank");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow text-primary">Contracts</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Annual Maintenance</h1>
          <p className="mt-1 text-sm text-muted-foreground">Track AMCs, renewal windows and visit consumption.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-border/70">
            <button data-testid="amc-view-list" onClick={() => setView("list")}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
              <List className="h-3.5 w-3.5" /> List
            </button>
            <button data-testid="amc-view-cards" onClick={() => setView("cards")}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium ${view === "cards" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
              <LayoutGrid className="h-3.5 w-3.5" /> Cards
            </button>
          </div>
          <button data-testid="amc-create-button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
            <Plus className="h-3.5 w-3.5" /> New AMC
          </button>
        </div>
      </header>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      {view === "cards" && amcs.length > 0 && (
        <div className="mb-3">
          <label className="inline-flex items-center gap-2 rounded-md border border-border/70 px-3 py-2 text-xs">
            <RowCheckbox testid="amc-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
            Select all
          </label>
        </div>
      )}

      {view === "list" ? (
        <div className="sharp-card overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-secondary/30 text-left">
              <tr>
                <th className="w-10 px-4 py-2">
                  <RowCheckbox testid="amc-list-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
                </th>
                <th className="px-4 py-2 label-eyebrow">Customer</th>
                <th className="px-4 py-2 label-eyebrow">Contract #</th>
                <th className="px-4 py-2 label-eyebrow">Start</th>
                <th className="px-4 py-2 label-eyebrow">End</th>
                <th className="px-4 py-2 label-eyebrow text-right">Value</th>
                <th className="px-4 py-2 label-eyebrow">Visits</th>
                <th className="px-4 py-2 label-eyebrow">PO</th>
                <th className="px-4 py-2 label-eyebrow">Actions</th>
              </tr>
            </thead>
            <tbody>
              {amcs.map((a) => (
                <tr key={a.id} data-testid={`amc-row-${a.id}`} className="border-b border-border/40 hover:bg-secondary/40">
                  <td className="px-4 py-2">
                    <RowCheckbox testid={`amc-select-${a.id}`} checked={selected.has(a.id)} onChange={() => toggleOne(a.id)} />
                  </td>
                  <td className="px-4 py-2 font-medium">{a.customer_name || "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{a.contract_no || "—"}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{fmtDate(a.start_date)}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{fmtDate(a.end_date)}</td>
                  <td className="px-4 py-2 text-right font-semibold">{fmtCurrency(a.contract_value)}</td>
                  <td className="px-4 py-2 text-xs">{a.visits_used}/{a.visits_total}</td>
                  <td className="px-4 py-2">
                    {a.po_document_id ? (
                      <div className="flex items-center gap-1">
                        <span className="max-w-[90px] truncate font-mono text-[11px] text-muted-foreground" title={a.po_number || ""}>{a.po_number || "PO"}</span>
                        <button data-testid={`amc-po-view-${a.id}`} title="View PO" onClick={() => openPO(a)} className="rounded-md border border-border/70 p-1 text-muted-foreground hover:text-primary"><Eye className="h-3.5 w-3.5" /></button>
                        <button data-testid={`amc-po-download-${a.id}`} title="Download PO" onClick={() => openPO(a, true)} className="rounded-md border border-border/70 p-1 text-muted-foreground hover:text-primary"><Download className="h-3.5 w-3.5" /></button>
                      </div>
                    ) : (a.po_number ? <span className="font-mono text-[11px] text-muted-foreground">{a.po_number}</span> : <span className="text-muted-foreground">—</span>)}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      <button data-testid={`amc-edit-${a.id}`} onClick={() => setEditing(a)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                      <button data-testid={`amc-delete-${a.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Delete AMC contract for ${a.customer_name}?`)) return;
                          try { await api.delete(`/amcs/${a.id}`); await load(); }
                          catch (e) { setErr(formatApiError(e)); }
                        }}
                        className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {amcs.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-xs text-muted-foreground">No contracts yet.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {amcs.map((a) => {
          const pct = a.visits_total ? Math.min(100, ((a.visits_used || 0) / a.visits_total) * 100) : 0;
          return (
            <div key={a.id} className={`sharp-card relative p-5 ${selected.has(a.id) ? "ring-2 ring-primary/60" : ""}`}>
              <div className="absolute right-3 top-3">
                <RowCheckbox testid={`amc-select-${a.id}`} checked={selected.has(a.id)} onChange={() => toggleOne(a.id)} />
              </div>
              <div className="flex items-center gap-3 pr-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-emerald-500/10 text-emerald-400">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="truncate font-heading text-base font-semibold">{a.customer_name || "—"}</p>
                  <p className="text-xs text-muted-foreground">{a.contract_no || "AMC contract"}</p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div><p className="label-eyebrow">Start</p><p className="mt-1">{fmtDate(a.start_date)}</p></div>
                <div><p className="label-eyebrow">End</p><p className="mt-1">{fmtDate(a.end_date)}</p></div>
                <div><p className="label-eyebrow">Value</p><p className="mt-1">{fmtCurrency(a.contract_value)}</p></div>
                <div><p className="label-eyebrow">Visits</p><p className="mt-1">{a.visits_used}/{a.visits_total}</p></div>
              </div>
              <div className="mt-3 h-1.5 overflow-hidden rounded bg-border">
                <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-3 flex justify-end gap-2">
                {a.po_document_id && (
                  <>
                    <button data-testid={`amc-card-po-view-${a.id}`} onClick={() => openPO(a)}
                      className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:border-primary/60 hover:text-primary">
                      <Eye className="h-3.5 w-3.5" /> PO
                    </button>
                    <button data-testid={`amc-card-po-download-${a.id}`} onClick={() => openPO(a, true)}
                      className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:border-primary/60 hover:text-primary">
                      <Download className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
                <button
                  data-testid={`amc-edit-${a.id}`}
                  onClick={() => setEditing(a)}
                  className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:border-primary/60 hover:text-primary"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  data-testid={`amc-delete-${a.id}`}
                  onClick={async () => {
                    if (!window.confirm(`Delete AMC contract for ${a.customer_name}?`)) return;
                    try { await api.delete(`/amcs/${a.id}`); await load(); }
                    catch (e) { setErr(formatApiError(e)); }
                  }}
                  className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:border-destructive/60 hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })}
        {amcs.length === 0 && <p className="col-span-full text-sm text-muted-foreground">No contracts yet.</p>}
      </div>
      )}

      <BulkBar count={selected.size} entityLabel="contracts" onClear={clearSel} onDelete={doBulkDelete} busy={bulkBusy} />

      {creating && <AMCModal customers={customers} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <AMCModal existing={editing} customers={customers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function AMCModal({ customers, onClose, onSaved, existing }) {
  const [form, setForm] = useState({
    customer_id: existing?.customer_id || "", contract_no: existing?.contract_no || "",
    start_date: existing?.start_date || "", end_date: existing?.end_date || "",
    visits_total: existing?.visits_total ?? 4, visits_used: existing?.visits_used ?? 0,
    contract_value: existing?.contract_value ?? 0, notes: existing?.notes || "",
    po_number: existing?.po_number || "", po_document_id: existing?.po_document_id || "",
  });
  const [poFile, setPoFile] = useState(null);
  const [poName, setPoName] = useState(existing?.po_document_name || "");
  const [uploadingPO, setUploadingPO] = useState(false);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadPO = async (file) => {
    if (!file) return;
    setUploadingPO(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "document");
      fd.append("folder", "AMC POs");
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, po_document_id: r.data.id }));
      setPoName(r.data.filename);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setUploadingPO(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = { ...form, po_document_name: poName || null, visits_total: Number(form.visits_total), visits_used: Number(form.visits_used), contract_value: Number(form.contract_value) };
      if (existing) await api.patch(`/amcs/${existing.id}`, payload);
      else await api.post("/amcs", payload);
      onSaved();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg sharp-card">
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold">{existing ? "Edit AMC contract" : "New AMC contract"}</h3>
          <button onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Customer *</label>
            <select required value={form.customer_id} onChange={set("customer_id")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">Select…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          {[
            ["contract_no", "Contract #", false],
            ["contract_value", "Value (INR)", false, "number"],
            ["start_date", "Start date *", true, "date"],
            ["end_date", "End date *", true, "date"],
            ["visits_total", "Total visits", false, "number"],
            ["visits_used", "Visits used", false, "number"],
          ].map(([k, l, req, type]) => (
            <div key={k}>
              <label className="label-eyebrow mb-1.5 block">{l}</label>
              <input required={!!req} type={type || "text"} value={form[k]} onChange={set(k)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
          ))}
          <div className="sm:col-span-2 rounded-md border border-dashed border-border/60 p-3">
            <p className="label-eyebrow mb-2">Purchase Order (PO)</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <label className="text-xs text-muted-foreground">PO number</label>
                <input data-testid="amc-po-number" value={form.po_number} onChange={set("po_number")} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="e.g. PO-2026-001" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">PO document</label>
                <div className="mt-1 flex items-center gap-2">
                  <input data-testid="amc-po-file" type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => { const f = e.target.files?.[0]; setPoFile(f); uploadPO(f); }} className="text-xs" />
                </div>
                {uploadingPO && <p className="mt-1 text-[11px] text-muted-foreground">Uploading…</p>}
                {form.po_document_id && !uploadingPO && (
                  <div className="mt-1 flex items-center gap-2">
                    <p className="text-[11px] text-emerald-500">✓ Uploaded: {poName || form.po_document_id}</p>
                    <button type="button" data-testid="amc-modal-po-view"
                      onClick={async () => {
                        try {
                          const r = await api.get(`/files/${form.po_document_id}`, { responseType: "blob" });
                          const url = URL.createObjectURL(r.data);
                          window.open(url, "_blank");
                          setTimeout(() => URL.revokeObjectURL(url), 60000);
                        } catch (e) { setErr(formatApiError(e)); }
                      }}
                      className="rounded-md border border-border/70 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground hover:text-primary">View</button>
                    <button type="button" data-testid="amc-modal-po-download"
                      onClick={async () => {
                        try {
                          const r = await api.get(`/files/${form.po_document_id}`, { responseType: "blob" });
                          const url = URL.createObjectURL(r.data);
                          const el = document.createElement("a");
                          el.href = url; el.download = poName || `PO-${form.po_number || "document"}`;
                          document.body.appendChild(el); el.click(); el.remove();
                          URL.revokeObjectURL(url);
                        } catch (e) { setErr(formatApiError(e)); }
                      }}
                      className="rounded-md border border-border/70 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground hover:text-primary">Download</button>
                  </div>
                )}
              </div>
            </div>
          </div>
          {err && <div className="sm:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : (existing ? "Save changes" : "Create AMC")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
